import React from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  GridProps,
  IconButton,
  Input,
  Typography,
} from '@mui/material';
import { CameraFilledIcon, ImageIcon, Upload } from 'components/SVG';
import { useField } from 'formik';
import { useImageUpload } from 'hooks/blobStorage/useImageUpload';
import Dropzone from 'react-dropzone';
import CloseIcon from '@mui/icons-material/Close';
import type { Accept } from 'react-dropzone';
import { useDialog } from 'hooks/common';
import { EditorModal } from 'sharedComponents/ImageEditor';
import { SparkImageLoader } from 'sharedComponents/UI';
import { useGetBlobSasToken } from 'hooks/useGetBlobSasToken';

const acceptedFileTypes: Accept = {
  'image/png': ['.png'],
  'image/svg+xml': ['.svg'],
  'image/jpg': ['.jpg'],
  'image/jpeg': ['.jpeg'],
};

type SparkFormImageUploaderProps = {
  name: string;
  containerName?: string;
  defaultUrl?: string;
  onUpload?: (url: string) => void;
  shouldDisplayImageAfterUpload?: boolean;
  size?: GridProps['xs'];
  gridProps?: GridProps;
  width?: string | number;
  onRemove?: () => void;
  canUpdateImage?: boolean;
  shouldRenderCloseButton?: boolean;
  showOnlyImageIcon?: boolean;
  disabled?: boolean;
  aspectRatio?: number;
};

function SparkFormImageUploader({
  name,
  containerName = 'temp',
  onUpload,
  size = 12,
  width = 150,
  shouldDisplayImageAfterUpload = true,
  shouldRenderCloseButton = false,
  canUpdateImage = true,
  defaultUrl,
  onRemove,
  showOnlyImageIcon = false,
  disabled = false,
  gridProps = {},
  aspectRatio,
}: SparkFormImageUploaderProps) {
  const [editImageURL, setEditImageURL] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');
  const { refetch: fetchToken } = useGetBlobSasToken(containerName);
  const fieldConfig = useField<string>(name);
  const field = fieldConfig[0];
  const helpers = fieldConfig[2];
  const { handleFileUpload, isUploading, progress } =
    useImageUpload(containerName);

  const {
    open: isEditorModalOpen,
    openDialog: openEditorModal,
    closeDialog: closeEditorModal,
  } = useDialog();

  const croppedImage = React.useCallback(
    async (file: File | null | undefined, srcUrl: string) => {
      if (!file) {
        return;
      }
      const url = await handleFileUpload(file);
      if (url) {
        onUpload && onUpload(url);
        helpers.setValue(url);
        fetchToken().then((data) => {
          const token = data.data?.data.attributes.token;
          setImageUrl(`${url}?${token}`);
        });
      }
    },
    [fetchToken, handleFileUpload, helpers, onUpload]
  );
  const onDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      const url = URL.createObjectURL(acceptedFiles[0]);
      setEditImageURL(url);
      openEditorModal();
    },
    [openEditorModal]
  );

  React.useEffect(() => {
    if (imageUrl) {
      return;
    }

    if (!field.value || defaultUrl) {
      return;
    }

    fetchToken().then((data) => {
      const token = data.data?.data.attributes.token;
      setImageUrl(`${defaultUrl || field.value}?${token}`);
    });
  }, [defaultUrl, fetchToken, field.value, imageUrl]);

  if (isUploading) {
    if (!showOnlyImageIcon) {
      return (
        <Grid
          container
          item
          xs={12}
          justifyContent='center'
          alignItems='center'
        >
          <Box
            border={(theme) => `5px dashed ${theme.palette.borderColor}`}
            borderRadius='20px'
            sx={{
              aspectRatio: 1,
              width: width,
            }}
            display='flex'
            justifyContent='center'
            alignItems='center'
            flexDirection='column'
          >
            <CircularProgress
              variant='determinate'
              value={progress}
              thickness={8}
            />
          </Box>
        </Grid>
      );
    } else {
      return (
        <>
          <Grid
            container
            item
            xs={size}
            justifyContent='center'
            alignItems='center'
          >
            <Box
              sx={{
                aspectRatio: 1,
                width: width,
              }}
              display='flex'
              justifyContent='center'
              alignItems='center'
              flexDirection='column'
            >
              <CircularProgress
                variant='determinate'
                value={progress}
                thickness={8}
                sx={{
                  width: `${width}px !important`,
                  height: `${width}px !important`,
                }}
              />
            </Box>
          </Grid>
        </>
      );
    }
  }

  return (
    <>
      <Grid
        container
        item
        xs={size}
        justifyContent='center'
        alignItems='center'
        {...gridProps}
      >
        <Box position='relative'>
          {imageUrl && shouldDisplayImageAfterUpload ? (
            <Box
              borderRadius='20%'
              sx={{
                aspectRatio: 1,
                width: width,
                overflow: 'hidden',
              }}
            >
              <SparkImageLoader
                src={imageUrl}
                alt=''
                width={width}
                height={width}
              />
            </Box>
          ) : showOnlyImageIcon ? (
            <></>
          ) : (
            <Box
              border={(theme) => `5px dashed ${theme.palette.borderColor}`}
              borderRadius='20px'
              sx={{
                aspectRatio: 1,
                width: width,
              }}
              display='flex'
              justifyContent='center'
              alignItems='center'
              flexDirection='column'
            >
              <Upload />
              <Typography>Upload Image</Typography>
            </Box>
          )}
          {canUpdateImage && (
            <Dropzone
              onDrop={onDrop}
              noDragEventsBubbling={true}
              preventDropOnDocument={true}
              multiple={false}
              accept={acceptedFileTypes}
              disabled={disabled}
            >
              {({ getRootProps, getInputProps }) => {
                return (
                  <Input
                    inputProps={{ ...getInputProps() }}
                    endAdornment={
                      showOnlyImageIcon ? (
                        <IconButton {...getRootProps()}>
                          <ImageIcon stroke={disabled ? 'gray' : 'black'} />
                        </IconButton>
                      ) : (
                        !disabled && (
                          <Box {...getRootProps()}>
                            <CameraFilledIcon />
                          </Box>
                        )
                      )
                    }
                    sx={{
                      '&:before': { display: 'none' },
                      position: 'absolute',
                      right: -10,
                      bottom: -10,
                      cursor: 'pointer',
                    }}
                  />
                );
              }}
            </Dropzone>
          )}
          {shouldRenderCloseButton && (
            <Box position='absolute' top={-5} right={-5}>
              <IconButton
                sx={{ bgcolor: (theme) => theme.palette.error.main }}
                size='small'
                onClick={onRemove}
              >
                <CloseIcon sx={{ width: 12, height: 12, color: 'white' }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Grid>
      {isEditorModalOpen && (
        <EditorModal
          onClose={closeEditorModal}
          open={isEditorModalOpen}
          imageSrc={editImageURL}
          getCroppedImage={croppedImage}
          aspectRatio={aspectRatio}
        />
      )}
    </>
  );
}

export default SparkFormImageUploader;
